// AstriaUKV2Shell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Sparkles,
  Heart,
  MessageCircle,
  Moon,
  Sun,
  UserCircle,
  Users,
  Waves,
} from "lucide-react";
import { AstriaUKV2ThemeProvider, useUKV2Colors } from "./AstriaUKV2ThemeContext";
import { UK_V2_HEADING_FONT, UK_V2_BODY_FONT, SHELL_COLORS_UK_V2 } from "./astriaUKV2Theme";

// ─── Astria UK V2 — shared shell (header + sticky tab bar + theme toggle) ──
// Calm-warm, understated British design system — per the client's UK Tone
// Engine v2 spec (Uk.txt "UK UI JSON"): light bg #F5F5F5 / dark bg #1C1C1C,
// light text #2E2E2E / dark text #EAEAEA, accent #4C4C4C, highlight #A3A3A3,
// clean-outline buttons, soft-glow hover, 14px card radius, 20px padding.

export { UK_V2_HEADING_FONT, UK_V2_BODY_FONT, SHELL_COLORS_UK_V2 };
export type { UKV2Palette } from "./astriaUKV2Theme";
export { useUKV2Colors } from "./AstriaUKV2ThemeContext";

export type AstriaUKV2Tab =
  | "zodiac-home"
  | "energy-match"
  | "matescan"
  | "relationship"
  | "compatibility"
  | "companion-talk"
  | "cosmic-uk"
  | "daily-flow"
  | "emotional-rhythm"
  | "zodiac-personality";

interface TabDef {
  key: AstriaUKV2Tab;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  path: string;
}

const TABS: TabDef[] = [
  {
    key: "zodiac-home",
    label: "Home",
    sublabel: "Choose Your Sign",
    icon: <span style={{ fontSize: 14 }}>✦</span>,
    path: "/astria-uk-v2/zodiac-home",
  },
  {
    key: "energy-match",
    label: "Energy Match",
    sublabel: "Analytical",
    icon: <Zap className="w-4 h-4" />,
    path: "/astria-uk-v2/energy-match",
  },
  {
    key: "matescan",
    label: "MateScan",
    sublabel: "Fun & Witty",
    icon: <Sparkles className="w-4 h-4" />,
    path: "/astria-uk-v2/matescan",
  },
  {
    key: "relationship",
    label: "Relationship",
    sublabel: "Insight",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-uk-v2/relationship",
  },
  {
    key: "compatibility",
    label: "Compatibility",
    sublabel: "Two Rhythms",
    icon: <Users className="w-4 h-4" />,
    path: "/astria-uk-v2/compatibility",
  },
  {
    key: "companion-talk",
    label: "Companion Talk",
    sublabel: "Inner Profile",
    icon: <MessageCircle className="w-4 h-4" />,
    path: "/astria-uk-v2/companion-talk",
  },
  {
    key: "cosmic-uk",
    label: "Cosmic UK",
    sublabel: "Grounded Magic",
    icon: <Moon className="w-4 h-4" />,
    path: "/astria-uk-v2/cosmic-uk",
  },
  {
    key: "daily-flow",
    label: "Daily Flow",
    sublabel: "Your Day",
    icon: <Sun className="w-4 h-4" />,
    path: "/astria-uk-v2/daily-flow",
  },
  {
    key: "emotional-rhythm",
    label: "Emotional Rhythm",
    sublabel: "Pacing",
    icon: <Waves className="w-4 h-4" />,
    path: "/astria-uk-v2/emotional-rhythm",
  },
  {
    key: "zodiac-personality",
    label: "Zodiac Personality",
    sublabel: "Core Traits",
    icon: <UserCircle className="w-4 h-4" />,
    path: "/astria-uk-v2/zodiac-personality",
  },
];

interface AstriaUKV2ShellProps {
  active: AstriaUKV2Tab;
  children: React.ReactNode;
}

const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme, colors } = useUKV2Colors();
  return (
    <button
      onClick={toggleTheme}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="flex items-center justify-center rounded-full transition-all flex-shrink-0"
      style={{
        width: 34,
        height: 34,
        border: `1px solid ${colors.cardBorder}`,
        background: colors.section,
        color: colors.accent,
      }}
    >
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
};

// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const UK_V2_MAX_WIDTH = 960;

const ShellInner: React.FC<AstriaUKV2ShellProps> = ({ active, children }) => {
  const navigate = useNavigate();
  const { colors } = useUKV2Colors();

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ background: colors.bg, fontFamily: UK_V2_BODY_FONT }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: colors.headerBg,
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: UK_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              border: `1.5px solid ${colors.accent}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: colors.card,
            }}
          >
            <span style={{ fontSize: 15, color: colors.accent }}>✦</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: colors.titleColor,
                fontFamily: UK_V2_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              Astria UK
            </div>
            <div style={{ fontSize: 11.5, color: colors.mutedColor }}>
              Calm, honest, a little dry — the British lane
            </div>
          </div>
          <ThemeToggle />
        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: UK_V2_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: isActive ? colors.white : colors.bodyColor,
                    background: isActive ? colors.accent : colors.section,
                    border: isActive ? "none" : `1px solid ${colors.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. Reacts to the
              light/dark theme toggle via colors.bg. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background: `linear-gradient(90deg, ${colors.bg}00 0%, ${colors.bg}E6 100%)`,
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: UK_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

const AstriaUKV2Shell: React.FC<AstriaUKV2ShellProps> = (props) => (
  <AstriaUKV2ThemeProvider>
    <ShellInner {...props} />
  </AstriaUKV2ThemeProvider>
);

export default AstriaUKV2Shell;
export { TABS as ASTRIA_UK_V2_TABS };
